import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as monaco from 'monaco-editor';
import Editor, { Monaco } from '@monaco-editor/react';
import type { IQueryFieldsItem } from 'src/interfaces/entities/observer';
import type { OcurrenceData } from 'src/interfaces/entities/occurence-data';
import {
	editorOptions,
	generateKeyDocs
} from 'src/pages/observers/components/PreviewMessageInput/configs';

export default function PreviewEditor(
	{
		sqlQuery,
		onChange,
		initialValue,
	}: {
		sqlQuery: IQueryFieldsItem[];
		onChange: (value: string) => void;
		initialValue?: string;
	}
) {
	const editorRef = useRef<monaco.editor.IStandaloneCodeEditor>();
	const [ editorValue, setEditorValue, ] = useState<string>(initialValue ?? '');

	const exampleObject: OcurrenceData = useMemo(
		() => ({
			occurence: {
				affectedRows: {
					result: [],
					count: 0,
				},
				affectedFields: {
					result: [],
					count: 0,
				},
				meta: {
					executionMS: 0,
					totalRowsCount: 0,
					executionDate: '',
				},
			},
			observer: {
				observerId: '',
				name: '',
			},
			severity: {
				name: '',
			},
		}),
		[]
	);

	const getSuggestions = useCallback(
		(object: any, path: string[]): monaco.languages.CompletionItem[] => {
			let properties = object;

			for (const key of path) {
				if (Array.isArray(properties)) {
					editorRef.current?.trigger(
						'keyboard',
						'editor.action.triggerSuggest',
						{}
					);
					properties = properties[0];
				} else if (properties && typeof properties === 'object') {
					properties = properties[key];
				} else {
					return [];
				}
			}

			if (properties && typeof properties === 'object') {
				return Object.keys(properties).map(
					//@ts-ignore
					(key): monaco.languages.CompletionItem => ({
						label: key,
						insertText: key,
						kind: monaco.languages.CompletionItemKind.Property,
						detail: 'Detalhes',
						documentation: generateKeyDocs(sqlQuery).find(
							(doc) => doc.property === path.join('.') + '.' + key
						)?.docs,
					})
				);
			}

			return [];
		},
		[]
	);

	const registerCompletionProvider = (object: OcurrenceData, monaco: Monaco) => {
		monaco.languages.registerCompletionItemProvider('twig', {
			triggerCharacters: [ '.', ],
			provideCompletionItems: (model, position) => {
				let suggestions: monaco.languages.CompletionItem[] = [];

				const wordInfo = model.getWordUntilPosition({
					lineNumber: position.lineNumber,
					column: position.column - 1,
				});
				const path = wordInfo.word.split('.').filter(Boolean);
				const fullPathRange = model.findPreviousMatch(
					/([a-zA-Z+]+\.)+/g as unknown as string,
					position,
					true,
					false,
					null,
					true
				)?.range;
				const fullPath = fullPathRange
					? model.getValueInRange(fullPathRange)
					: '';
				const fullPathParts = fullPath.split('.').filter(Boolean);

				if (path.length > 0) {
					try {
						suggestions = getSuggestions(object, fullPathParts);
					} catch (error) {
						console.error(
							'Invalid path for suggestions:',
							path,
							error
						);
					}
				}

				if (suggestions.length === 0) {
					//@ts-ignore
					suggestions.push({
						label: 'occurence',
						insertText: 'occurence${1}',
						kind: monaco.languages.CompletionItemKind.Variable,
						insertTextRules:
							monaco.languages.CompletionItemInsertTextRule
								.InsertAsSnippet,
						detail: 'Detalhes',
						documentation: generateKeyDocs().find(
							(doc) => doc.property === 'occurence'
						)?.docs,
					});

					//@ts-ignore
					suggestions.push({
						label: 'observer',
						insertText: '{{observer${1}}}',
						kind: monaco.languages.CompletionItemKind.Variable,
						insertTextRules:
							monaco.languages.CompletionItemInsertTextRule
								.InsertAsSnippet,
						detail: 'Detalhes',
						documentation: generateKeyDocs().find(
							(doc) => doc.property === 'observer'
						)?.docs,
					});
					//@ts-ignore
					suggestions.push({
						label: 'severity',
						insertText: '{{severity${1}}}',
						kind: monaco.languages.CompletionItemKind.Variable,
						insertTextRules:
							monaco.languages.CompletionItemInsertTextRule
								.InsertAsSnippet,
						detail: 'Detalhes',
						documentation: generateKeyDocs().find(
							(doc) => doc.property === 'severity'
						)?.docs,
					});
				}

				return { suggestions: suggestions, };
			},
		});
	};

	const beforeMount = (monaco: Monaco) => {
		registerCompletionProvider(exampleObject, monaco);
	};

	useEffect(() => {
		onChange(editorValue);
	}, [ editorValue, ]);

	return (
		<Editor
			value={editorValue}
			onChange={(val) => setEditorValue(val ?? '')}
			height={95}
			language='twig'
			beforeMount={beforeMount}
			options={editorOptions}
		/>
	);
}
